/**
 * CurvedSlider
 *
 * Draggable curved slider
 */
class CurvedSlider {
    /**
     *
     * @param {HTMLElement} canvasEl
     * @param {{}}options
     * @param {Boolean} init
     */
    constructor(canvasEl, options = {}, init = false) {
        this._setCanvas(canvasEl);
        this._setCTX();
        this._initAnswer();
        this.setOptions(options);

        // Init draggable to false, this is detected with mouse down event
        this.draggable        = false;
        this.isInMiddleAnswer = false;

        // Get canvas center
        this.knobCenterX = this.getCanvas().width / 2;
        this.knobCenterY = this.getCanvas().height / 2;

        // Listeners
        this.listeners();

        // Do we have to display the canvas at start?
        if (init) {
            this._init();
        }
    }

    /**
     * Get offset for a given element
     *
     * @param {HTMLElement} elt
     * @returns {{top: number, left: number}}
     * @private
     */
    static _getOffset(elt) {
        const rect = elt.getBoundingClientRect();

        return {
            top: rect.top,
            left: rect.left
        }
    }

    answer(response) {
        let position = {};
        response     = parseInt(response);

        switch (response) {
            case CurvedSlider.ANSWER_2:
                this._setAnswer(CurvedSlider.ANSWER_2);
                position = this.getThirdAnswerPosition();
                break;

            case CurvedSlider.ANSWER_1:
                this._setAnswer(CurvedSlider.ANSWER_1);
                position = this.getSecondAnswerPosition();
                break;

            case CurvedSlider.ANSWER_0:
                this._setAnswer(CurvedSlider.ANSWER_0);
                position = this.getFirstAnswerPosition();
                break;

            default:
                throw new Error('Incorrect answer given. Available are 0, 1 or 2. Given ' + response);
        }

        this.draw(position['x'], position['y']);
    }

    /**
     * Init the curved slider
     *
     * @private
     */
    _init() {
        this.answer(undefined === this.getAnswer() ? this.options.defaultAnswer : this.getAnswer());
    }

    /**
     * Reload the Curved Slider
     */
    reload() {
        this._init();
    }

    /**
     * Init the answer chosen.
     * Makes it a private property
     *
     * @private
     */
    _initAnswer() {
        let answer;
        /**
         * Get answer value
         *
         * @returns {int}
         */
        this.getAnswer = () => {
            return answer;
        };
        /**
         * Set the answer chosen
         *
         * @param {String} asw see all CurvedSlider.ANSWER_* constants
         * @private
         */
        this._setAnswer = (asw) => {
            answer = asw;
        };
    }

    /**
     * Draw the curved slider on canvas
     *
     * @param {int} mouseX
     * @param {int} mouseY
     */
    draw(mouseX, mouseY) {
        const canvasOffset = CurvedSlider._getOffset(this.getCanvas());
        this.offsetX       = canvasOffset.left;
        this.offsetY       = canvasOffset.top;

        // Start drawing
        this.getCTX().clearRect(0, 0, this.getCanvas().width, this.getCanvas().height);

        // Draw knob
        this.drawKnob();

        // Draw no on knob
        const noPosition = this.getFirstAnswerPosition();
        this.drawFixedCursor(noPosition);

        // Draw yes on knob
        const yesPosition = this.getThirdAnswerPosition();
        this.drawFixedCursor(yesPosition);

        // Defined draggable cursor position
        let draggablePosition = this._defineDraggableCursorPosition(mouseX, mouseY, noPosition, yesPosition);

        // Draw hidden areas, this may alter the draggablePosition
        draggablePosition = this._createTopArea(draggablePosition, {x: mouseX, y: mouseY});
        draggablePosition = this._createBottomLeftArea(draggablePosition);
        draggablePosition = this._createBottomRightArea(draggablePosition);

        // Draw the draggable cursor
        this.drawDraggableCursor(draggablePosition);

        // Draw answers
        this.drawAnswers();
    }

    /**
     * Get the "Yes in part" answer position on the knob
     *
     * @returns {{x: *, y: *}}
     */
    getSecondAnswerPosition() {
        // Given mousePosition, what is the nearest point on the knob
        const rads = Math.atan2(1 - this.knobCenterY, this.getCanvas().width / 2 - this.knobCenterX);

        return {
            x: this.options.knobRadius * Math.cos(rads) + this.knobCenterX,
            y: this.options.knobRadius * Math.sin(rads) + this.knobCenterY
        };
    }

    /**
     * Get the "No" answer position on the knob
     *
     * @returns {{x: *, y: *}}
     */
    getFirstAnswerPosition() {
        const rads = Math.atan2(265 - this.knobCenterY, -this.knobCenterX);
        const arcX = this.options.knobRadius * Math.cos(rads) + this.knobCenterX;
        const arcY = this.options.knobRadius * Math.sin(rads) + this.knobCenterY;

        return {x: arcX, y: arcY};
    }

    /**
     * Get the "No" answer position on the knob
     *
     * @returns {{x: *, y: *}}
     */
    getThirdAnswerPosition() {
        const rads = Math.atan2(260 - this.knobCenterY, this.knobCenterX);
        const arcX = this.options.knobRadius * Math.cos(rads) + this.knobCenterX;
        const arcY = this.options.knobRadius * Math.sin(rads) + this.knobCenterY;

        return {x: arcX, y: arcY};
    }

    /**
     * Draw fixed cursor on knob
     *
     * @param {{x: *, y: *}} position
     */
    drawFixedCursor(position) {
        this.getCTX().beginPath();
        this.getCTX().arc(position["x"], position["y"], this.options.indicatorRadius, 0, Math.PI * 2, false);
        this.getCTX().fillStyle = this.options.knobColor;
        this.getCTX().fill();
    }

    /**
     * Canvas listeners
     */
    listeners() {
        // Detect touch and then automatically assign events
        const isTouchSupported = 'ontouchstart' in window;
        const startEvent       = isTouchSupported ? 'touchstart' : 'mousedown';
        const moveEvent        = isTouchSupported ? 'touchmove' : 'mousemove';
        const endEvent         = isTouchSupported ? 'touchend' : 'mouseup';

        this.getCanvas().addEventListener(startEvent, (e) => {
            this.draggable = true;
            this.eventType = CurvedSlider.DD_START;
            this.handleMouseDown(e);
        }, false);

        this.getCanvas().addEventListener(endEvent, (e) => {
            this.draggable = false;
            this.eventType = CurvedSlider.DD_END;

            if (this.isInMiddleAnswer === false) {
                this.handleMouseDown(e);
            }
            this.isInMiddleAnswer = false;
        }, false);

        this.getCanvas().addEventListener(moveEvent, (e) => {
            if (this.draggable) {
                this.eventType = CurvedSlider.DD_MOVE;
                this.handleMouseDown(e);
            }
        }, false);

        // Prevent page from scrolling when scrolling into the canvas
        document.addEventListener(moveEvent, (e) => {
            if (e.target.getAttribute('id') === this.getCanvas().getAttribute('id')) {
                e.preventDefault();
            }
        }, {passive: false});
    }

    /**
     * Handle mouse down event
     *
     * @param e
     */
    handleMouseDown(e) {
        let el;
        if (e.changedTouches && e.changedTouches.length > 0) {
            el = e.changedTouches[0];
        } else {
            el = (e.targetTouches && e.targetTouches.length > 0) ? e.targetTouches[0] : e;
        }

        const mouseX = el.clientX - this.offsetX;
        const mouseY = el.clientY - this.offsetY;

        this.draw(mouseX, mouseY);
    }

    /**
     * Set curved slider canvas and add a getter
     *
     * @param {HTMLElement} canvasEl
     * @private
     */
    _setCanvas(canvasEl) {
        const canvas = canvasEl;
        /**
         * Return the canvas
         *
         * @returns {HTMLElement}
         */
        this.getCanvas = () => {
            return canvas;
        };
    }

    /**
     * Set the curved slider options
     *
     * @param {{}}options
     * @returns {CurvedSlider}
     */
    setOptions(options) {
        this.options = Object.assign({
            startAngle: 0.8 * Math.PI,
            endAngle: Math.PI * 2.2,
            knobRadius: 100,
            knobColor: "#EAEAEA",
            indicatorRadius: 15,
            indicatorColor: "#EE334E",
            textColor: '#1C2671',
            cursorShadowColor: 'rgba(0, 0, 0, 0.16)',
            cursorStrokeColor: '#F7F7F7',
            answers: {
                0: 'Non',
                1: {
                    0: 'Oui,',
                    1: 'en partie'
                },
                2: 'Oui'
            },
            defaultAnswer: 0,
            lineHeight: 30,
        }, options);

        return this;
    }

    /**
     * Set context from canvas and add a getter
     *
     * @private
     */
    _setCTX() {
        const CTX = this.getCanvas().getContext("2d");

        /**
         * Get canvas context
         *
         * @returns {CanvasRenderingContext2D | WebGLRenderingContext | *}
         */
        this.getCTX = () => {
            return CTX;
        }
    }

    /**
     * Draw the draggable cursor on knob
     * @param {{}} draggablePosition
     */
    drawDraggableCursor(draggablePosition) {
        this.getCTX().beginPath();
        this.getCTX().arc(draggablePosition.x, draggablePosition.y, this.options.indicatorRadius, 0, Math.PI * 2, false);
        this.getCTX().fillStyle = this.options.indicatorColor;
        this.getCTX().fill();
        this.getCTX().shadowColor   = this.options.cursorShadowColor;
        this.getCTX().shadowBlur    = 3;
        this.getCTX().shadowOffsetY = 3;
        this.getCTX().lineWidth     = 5;
        this.getCTX().strokeStyle   = this.options.cursorStrokeColor;
        this.getCTX().stroke();
        this.getCTX().shadowBlur    = 0;
        this.getCTX().shadowOffsetY = 0;
    }

    /**
     * Draw all answers
     */
    drawAnswers() {
        this._drawMultilineAnswer(this.options.answers[0], this.knobCenterX - 80, this.knobCenterY + 120);
        this._drawMultilineAnswer(this.options.answers[2], this.knobCenterX + 72, this.knobCenterY + 120);
        this._drawMultilineAnswer(this.options.answers[1], this.knobCenterX, this.knobCenterY - (this.options.lineHeight + 20));
    }

    /**
     * Handle the case the answer is displayed on multi lines
     *
     * @param {string}answer
     * @param {integer}x
     * @param {integer}y
     * @private
     */
    _drawMultilineAnswer(answer, x, y) {
        // Multine
        if (typeof answer === 'object') {
            const answerTexts = Object.keys(answer).map(function (e) {
                return answer[e]
            });

            // Fist line is in bold
            this.getCTX().font = 'bold 24px "Open Sans",sans-serif';
            this.getCTX().fillText(answerTexts[0], x, y);

            // Other lines are lighter
            this.getCTX().font = 'lighter 24px "Open Sans",sans-serif';
            for (let i = 1; i <= (answerTexts.length - 1); i++) {
                let space = y + (i * this.options.lineHeight);
                this.getCTX().fillText(answerTexts[i], x, space);
            }
        }
        // One line
        else {
            this.getCTX().font      = '600 24px "Open Sans",sans-serif';
            this.getCTX().fillStyle = this.options.textColor;
            this.getCTX().textAlign = 'center';
            this.getCTX().fillText(answer, x, y);
        }
    }

    /**
     * Draw the Knob
     */
    drawKnob() {
        this.getCTX().beginPath();
        this.getCTX().arc(this.knobCenterX, this.knobCenterY, this.options.knobRadius, this.options.startAngle, this.options.endAngle, false);
        this.getCTX().fillStyle = "transparent";
        this.getCTX().fill();
        this.getCTX().lineWidth   = 15;
        this.getCTX().strokeStyle = this.options.knobColor;
        this.getCTX().stroke();
    }

    /**
     *
     * @param mouseX
     * @param mouseY
     * @param noPosition
     * @param yesPosition
     *
     * @returns {{x: *, y: *}}
     *
     * @private
     */
    _defineDraggableCursorPosition(mouseX, mouseY, noPosition, yesPosition) {
        // Given mousePosition, what is the nearest point on the knob
        const rads     = Math.atan2(mouseY - this.knobCenterY, mouseX - this.knobCenterX);
        let indicatorX = this.options.knobRadius * Math.cos(rads) + this.knobCenterX;
        let indicatorY = this.options.knobRadius * Math.sin(rads) + this.knobCenterY;

        if (indicatorY > yesPosition['y']) {
            indicatorY       = yesPosition['y'];
            const forbiddenX = yesPosition['x'] - noPosition['x'];
            const near       = forbiddenX / 2;
            if (indicatorX < yesPosition['x'] && indicatorX >= yesPosition['x'] - near) {
                indicatorX = yesPosition['x'];
            }

            if (indicatorX > noPosition['x'] && indicatorX <= noPosition['x'] + near) {
                indicatorX = noPosition['x'];
            }
        }

        return {x: indicatorX, y: indicatorY}
    }

    /**
     * Create the top area in which we can drag the cursor to select the "Yes in part" answer
     * This also handle click on the answer directly
     *
     * @param {{x: *, y: *}} draggablePosition
     * @param {{x: *, y: *}} mousePosition
     *
     * @returns {{x: *, y: *}}
     *
     * @private
     */
    _createTopArea(draggablePosition, mousePosition) {
        this.getCTX().beginPath();
        this.getCTX().rect(this.knobCenterX - 60, this.knobCenterY - 50, 120, 55);
        // this.getCTX().fillStyle = 'green';
        this.getCTX().fillStyle = 'transparent';
        this.getCTX().fill();

        // Listen click on the "Yes in part" text answer
        if (this.eventType === CurvedSlider.DD_START && this.getCTX().isPointInPath(mousePosition['x'], mousePosition['y'])) {
            this.isInMiddleAnswer = true;
            draggablePosition     = this.getSecondAnswerPosition();
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_1);
            this._setAnswer(CurvedSlider.ANSWER_1);
            this.draggable = false;
        }

        this.getCTX().beginPath();
        this.getCTX().rect(this.knobCenterX - 100, this.knobCenterY - 120, 200, 70);
        // this.getCTX().fillStyle = 'rgb(255,0,0, 0.4)';
        this.getCTX().fillStyle = 'transparent';
        this.getCTX().fill();

        // Listen for drag & drop. If it ends in the yesInPart area, we set cursor at center top of the knob
        if (this.eventType === CurvedSlider.DD_END && this.getCTX().isPointInPath(draggablePosition['x'], draggablePosition['y'])) {
            draggablePosition = this.getSecondAnswerPosition();
            this._setAnswer(CurvedSlider.ANSWER_1);
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_1);
        }

        return draggablePosition;
    }

    _triggerSelectYes(position, withGlobalEvent = true) {
        this._triggerEvent(CurvedSlider.ANSWER_2, position);
        if (withGlobalEvent) {
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_2);
        }
    }

    _triggerSelectNo(position, withGlobalEvent = true) {
        this._triggerEvent(CurvedSlider.ANSWER_0, position);
        if (withGlobalEvent) {
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_0);
        }
    }

    /**
     * Create the top area in which we can drag the cursor to select the "No" answer
     *
     * @param {{x: *, y: *}} draggablePosition
     *
     * @returns {{x: *, y: *}}
     *
     * @private
     */
    _createBottomLeftArea(draggablePosition) {
        this.getCTX().beginPath();
        this.getCTX().rect(this.knobCenterX - 110, this.knobCenterY - 50, 50, 130);
        // this.getCTX().fillStyle = 'rgb(0,0,255, 0.4)';
        this.getCTX().fillStyle = 'transparent';
        this.getCTX().fill();

        // Listen for drag & drop. If it ends in the yesInPart area, we set cursor at center top of the knob
        if (this.eventType === CurvedSlider.DD_END && this.getCTX().isPointInPath(draggablePosition['x'], draggablePosition['y'])) {
            draggablePosition = this.getFirstAnswerPosition();
            this._setAnswer(CurvedSlider.ANSWER_0);
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_0);
        }

        return draggablePosition;
    }

    /**
     *
     * Create the top area in which we can drag the cursor to select the "No" answer
     *
     * @param {{x: *, y: *}} draggablePosition
     *
     * @returns {{x: *, y: *}}
     *
     * @private
     */
    _createBottomRightArea(draggablePosition) {
        this.getCTX().beginPath();
        this.getCTX().rect(this.knobCenterX + 60, this.knobCenterY - 50, 50, 130);
        // this.getCTX().fillStyle = 'rgb(255,255,0, 0.4)';
        this.getCTX().fillStyle = 'transparent';
        this.getCTX().fill();

        // Listen for drag & drop. If it ends in the yesInPart area, we set cursor at center top of the knob
        if (this.eventType === CurvedSlider.DD_END && this.getCTX().isPointInPath(draggablePosition['x'], draggablePosition['y'])) {
            draggablePosition = this.getThirdAnswerPosition();
            this._setAnswer(CurvedSlider.ANSWER_2);
            this._triggerEvent(CurvedSlider.EVENT_VALUE_CHANGED, CurvedSlider.ANSWER_2);
        }

        return draggablePosition;
    }

    /**
     * Trigger an event
     *
     * @param {String} eventName
     * @param {{}} data
     *
     * @private
     */
    _triggerEvent(eventName, data) {
        if (window.CustomEvent) {
            const event = new CustomEvent(eventName, {bubbles: true, cancelable: false, detail: data});
            this.getCanvas().dispatchEvent(event);
        } else {
            const event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventName, true, true, data);
            this.getCanvas().dispatchEvent(event);
        }
    }

    /**
     * Listen for custom events
     *
     * @param {string} eventName
     * @param {callback} cb
     */
    addEventListener(eventName, cb) {
        if (CurvedSlider.EVENT_VALUE_CHANGED.indexOf(eventName) === -1) {
            console.warn('Invalid ' + eventName + ' event on CurvedSlider class');
        } else {
            this.getCanvas().addEventListener(eventName, cb);
        }
    }
}

/*
 * Possible answer value
 */
CurvedSlider.ANSWER_0 = 0;
CurvedSlider.ANSWER_1 = 1;
CurvedSlider.ANSWER_2 = 2;

/*
 * Triggerable and listenable Events
 */
CurvedSlider.EVENT_VALUE_CHANGED = 'valueChanged';

/*
 * Drag and drop
 */
CurvedSlider.DD_START = 'dragAndDropStart';
CurvedSlider.DD_END   = 'dragAndDropEnd';
CurvedSlider.DD_MOVE  = 'dragAndDropMove';
